import { STRIPE_URL } from '@/constants/constants';
import { SubscriptionItemData } from '@/types/stripe.types';
import {
  retrieveStripeAccount,
  retrieveStripeCustomer,
  retrieveStripeInvoicesList,
  retrieveStripePaymentMethodList,
  retrieveStripeSubscription,
  retrieveStripeTaxIdsList,
  stripeAddItemsToSubscription,
  updateBillingInfo,
} from '@/utils';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

export const stripeApiSlice = createApi({
  reducerPath: 'stripe',
  tagTypes: ['stripe', 'sub'],
  baseQuery: fetchBaseQuery({ baseUrl: STRIPE_URL }),
  endpoints: (builder) => ({
    getStripeCustomer: builder.query({
      queryFn: async (id: string | undefined) => {
        if (!id) {
          throw new Error('Customer ID is undefined');
        }
        const data = await retrieveStripeCustomer(id);
        return { data };
      },
      providesTags: () => [{ type: 'stripe' }],
    }),
    getSubscription: builder.query({
      queryFn: async (id: string | undefined) => {
        if (!id) {
          throw new Error('Subscription ID is undefined');
        }
        const data = await retrieveStripeSubscription(id);
        return { data };
      },
      providesTags: () => [{ type: 'sub' }],
    }),
    getStripeAccount: builder.query({
      queryFn: async (id: string | undefined) => {
        if (!id) {
          throw new Error('Customer ID is undefined');
        }
        const data = await retrieveStripeAccount(id);
        return { data };
      },
      providesTags: () => [{ type: 'stripe' }],
    }),
    getPaymentMethodList: builder.query({
      queryFn: async (customerId: string | undefined) => {
        if (!customerId) {
          throw new Error('Customer ID is undefined');
        }
        const data = await retrieveStripePaymentMethodList(customerId);
        return { data };
      },
      providesTags: () => [{ type: 'stripe' }],
    }),
    getStripeTaxIdList: builder.query({
      queryFn: async (customerId: string | undefined) => {
        if (!customerId) {
          throw new Error('Customer ID is undefined');
        }
        const taxesList = await retrieveStripeTaxIdsList(customerId);
        return { data: taxesList?.data };
      },
      providesTags: () => [{ type: 'stripe' }],
    }),
    getInvoicesList: builder.query({
      queryFn: async (customerId: string | undefined) => {
        if (!customerId) {
          throw new Error('Customer ID is undefined');
        }
        const data = await retrieveStripeInvoicesList(customerId);
        return { data };
      },
      providesTags: () => [{ type: 'stripe' }],
    }),
    addItemsToSubscription: builder.mutation({
      queryFn: async ({ item }: { item: SubscriptionItemData; id: string }) => {
        const data = await stripeAddItemsToSubscription(item);
        return { data };
      },
      invalidatesTags: () => [{ type: 'sub' }],
    }),
    updateBillingInfo: builder.mutation({
      queryFn: async (billingInfo) => {
        const data = await updateBillingInfo(billingInfo);
        return { data };
      },
      invalidatesTags: () => [{ type: 'stripe' }],
    }),
  }),
});

export const {
  useGetStripeCustomerQuery,
  useGetSubscriptionQuery,
  useGetStripeAccountQuery,
  useGetPaymentMethodListQuery,
  useGetInvoicesListQuery,
  useGetStripeTaxIdListQuery,
  useAddItemsToSubscriptionMutation,
  useUpdateBillingInfoMutation,
} = stripeApiSlice;
